import { BaseResponse, ErrorCodes } from "../types/errors";
import { readApiErrorEnvelope } from "../api/apiErrorEnvelope";
import i18n from "../i18n";

/**
 * Formats an error message with parameters
 */
function formatMessage(
  template: string | undefined,
  params?: Record<string, string>,
): string {
  if (!template) return "";
  if (!params) return template;

  let message = template;
  Object.entries(params).forEach(([key, value]) => {
    message = message.replace(new RegExp(`\\{${key}\\}`, "g"), value);
  });
  return message;
}

/**
 * Gets a localized error message for an error code
 */
export function getErrorMessage(
  errorCode: ErrorCodes | keyof typeof ErrorCodes,
  params?: Record<string, string>,
): string {
  // Special handling for Exception error code
  if (
    errorCode === ErrorCodes.Exception &&
    params?.exceptionType &&
    params?.message
  ) {
    return `Chyba ${params.exceptionType}\n${params.message}`;
  }

  // ErrorCodes is a string enum mirroring the backend names, so the wire value
  // IS the translation key suffix. Any unknown value (e.g. legacy numeric codes
  // from older callers) falls through to the "unknown code" branch.
  const enumName =
    typeof errorCode === "string" && (errorCode as string) in ErrorCodes
      ? (errorCode as string)
      : undefined;

  if (!enumName) {
    return `Nastala chyba (neznámý kód: ${errorCode})`;
  }

  // Use enum name as translation key
  const messageKey = `errors.${enumName}`;
  const message = i18n.t(messageKey);

  // If translation not found, return generic error with enum name
  if (message === messageKey || !message) {
    return `Nastala chyba (kód: ${enumName})`;
  }

  const formatted = formatMessage(message, params);
  return formatted || `Nastala chyba (kód: ${enumName})`;
}

/**
 * Handles API error responses
 */
export function handleApiError(response: BaseResponse): string {
  if (response.success) {
    return "";
  }

  if (!response.errorCode) {
    return "Nastala neznámá chyba";
  }

  return getErrorMessage(response.errorCode, response.params);
}

/**
 * Checks if a response is an error response
 */
export function isErrorResponse(response: any): response is BaseResponse {
  return (
    response != null &&
    typeof response === "object" &&
    "success" in response &&
    response.success === false
  );
}

/**
 * Resolves a failed generated-client call to a Czech message, or `undefined` when the
 * failure carries no structured error envelope (network failure, 500, unparseable body)
 * so the caller can fall back to its own generic message.
 *
 * The envelope parsing itself is delegated to `readApiErrorEnvelope` — the shared reader
 * that also understands a directly-thrown envelope and normalises a null `params` — so
 * this file never re-implements it. Callers: `PriceAnalysis` (a rejected edit gets an
 * inline cell message, anything else a toast + stale-totals badge) and
 * `PricingScenarioBar` (save/delete failures).
 */
export function resolveSwaggerErrorMessage(error: unknown): string | undefined {
  const envelope = readApiErrorEnvelope(error);
  if (!envelope?.errorCode) {
    return undefined;
  }

  return handleApiError({
    success: false,
    errorCode: envelope.errorCode,
    params: envelope.params,
  });
}

/**
 * Message to show for a failed generated-client call: the translated business message
 * when the failure carries an error envelope, otherwise the thrown `Error`'s own message,
 * otherwise `fallback`.
 */
export function resolveApiErrorMessage(error: unknown, fallback: string): string {
  return (
    resolveSwaggerErrorMessage(error) ??
    (error instanceof Error ? error.message : fallback)
  );
}

/**
 * Extract error message from various error types
 */
export function extractErrorMessage(error: any): string {
  // Handle API error responses
  if (isErrorResponse(error)) {
    return handleApiError(error);
  }

  // Handle axios/fetch response with data
  if (error?.response?.data && isErrorResponse(error.response.data)) {
    return handleApiError(error.response.data);
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === "string") {
    return error;
  }

  // Default fallback
  return "Nastala neočekávaná chyba";
}
